import { create } from "zustand";
import type { AuthUser } from "@/types";
import {
  clearTokens,
  getAccessToken,
  setTokens,
  setTenantId,
  setIndustryId,
} from "@/services/tokenStorage";
import {
  fetchCurrentUser,
  passwordLogin,
  manualSignup,
  completeGoogleOnboarding,
  acceptInvitation,
  type TokenResponse,
} from "@/services/authService";

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  hydrated: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signup: (params: {
    name: string;
    email: string;
    password: string;
    company_name: string;
    industry_id?: string;
    invitation_token?: string;
  }) => Promise<void>;
  completeOnboarding: (params: {
    onboarding_token: string;
    company_name: string;
    industry_id?: string;
  }) => Promise<void>;
  acceptInvite: (params: {
    token: string;
    name?: string;
    password?: string;
    onboarding_token?: string;
  }) => Promise<void>;
  setSessionFromTokens: (tokens: TokenResponse) => Promise<void>;
  logout: () => void;
};

function persistWorkspaceHeaders(user: AuthUser) {
  if (user.tenantId) setTenantId(user.tenantId);
  if (user.industryId) setIndustryId(user.industryId);
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  hydrated: false,
  error: null,
  hydrate: async () => {
    const token = getAccessToken();
    if (!token) {
      set({ user: null, hydrated: true, loading: false });
      return;
    }
    set({ loading: true });
    try {
      const user = await fetchCurrentUser(token);
      persistWorkspaceHeaders(user);
      set({ user, hydrated: true, loading: false, error: null });
    } catch {
      clearTokens();
      set({ user: null, hydrated: true, loading: false });
    }
  },
  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const tokens = await passwordLogin(email.trim(), password);
      setTokens(tokens.access_token, tokens.refresh_token);
      const user = await fetchCurrentUser(tokens.access_token);
      persistWorkspaceHeaders(user);
      set({ user, loading: false, hydrated: true, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      set({ loading: false, error: message, user: null });
      throw err;
    }
  },
  signup: async (params) => {
    set({ loading: true, error: null });
    try {
      const tokens = await manualSignup(params);
      setTokens(tokens.access_token, tokens.refresh_token);
      const user = await fetchCurrentUser(tokens.access_token);
      persistWorkspaceHeaders(user);
      set({ user, loading: false, hydrated: true, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Signup failed";
      set({ loading: false, error: message, user: null });
      throw err;
    }
  },
  completeOnboarding: async (params) => {
    set({ loading: true, error: null });
    try {
      const tokens = await completeGoogleOnboarding(params);
      setTokens(tokens.access_token, tokens.refresh_token);
      const user = await fetchCurrentUser(tokens.access_token);
      persistWorkspaceHeaders(user);
      set({ user, loading: false, hydrated: true, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Onboarding failed";
      set({ loading: false, error: message, user: null });
      throw err;
    }
  },
  acceptInvite: async (params) => {
    set({ loading: true, error: null });
    try {
      const tokens = await acceptInvitation(params);
      setTokens(tokens.access_token, tokens.refresh_token);
      const user = await fetchCurrentUser(tokens.access_token);
      persistWorkspaceHeaders(user);
      set({ user, loading: false, hydrated: true, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to accept invitation";
      set({ loading: false, error: message, user: null });
      throw err;
    }
  },
  setSessionFromTokens: async (tokens) => {
    setTokens(tokens.access_token, tokens.refresh_token);
    const user = await fetchCurrentUser(tokens.access_token);
    persistWorkspaceHeaders(user);
    set({ user, loading: false, hydrated: true, error: null });
  },
  logout: () => {
    clearTokens();
    set({ user: null, error: null });
  },
}));
